import {Fieldset, Text} from '@albert-io/atomic';
import type {QuestionModelV3} from '@albert-io/models';
import classnames from 'classnames';
import {setUpStore, useFluxStore} from 'client/framework';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {List} from 'immutable';
import React, {useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {
  translatedInstructions,
  useQuestionTranslationStore
} from 'components/QuestionTypes/shared/TranslationSection';

import FillInTheBlankQuestionStore from './FillInTheBlankQuestion.store';

import './fill-in-the-blank.scss';

interface FillInTheBlankInputAreaProps {
  storeName?: string;
  question?: QuestionModelV3;
  questionId?: string;
  isGuessSubmitted?: boolean;
}

/*
old propTypes info
  static propTypes = {
    storeName: PropTypes.string,
    question: ImmutablePropTypes.record,
    questionId: PropTypes.bool,
    isGuessSubmitted: PropTypes.bool
  };

  static defaultProps = {
    storeName: 'fillInTheBlankQuestionStore'
  };
*/

const FillInTheBlankInputArea = ({
  question,
  questionId,
  isGuessSubmitted,
  storeName = 'fillInTheBlankQuestionStore'
}: FillInTheBlankInputAreaProps) => {
  const store = useMemo(() => {
    return setUpStore(FillInTheBlankQuestionStore, storeName, questionId);
  }, [storeName, questionId]);
  // Bridge: without this subscription, surfaces outside the legacy re-render
  // cascade (e.g. diagnostics) never repaint the dropdowns after a selection.
  useFluxStore(store, (s) => s.readData());

  // Translation info
  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const [dropdowns, dropdownText] = useMemo(() => {
    if (showTranslations && translatedQuestionInfo) {
      return [
        formatDropdowns(translatedQuestionInfo.dropdowns),
        translatedQuestionInfo.dropdown_text
      ];
    }
    return [question.getDropdowns(), question.getDropdownText()];
  }, [question, translatedQuestionInfo, showTranslations]);

  return (
    <Fieldset>
      <Text as='legend' size='m' className='mb-x1' bold>
        {showTranslations ? translatedInstructions.dropdown : 'Select options below'}
      </Text>
      <MarkdownRendererV2
        className={classnames('fitb-input-area', {
          'fitb-input-area-dropdown': !dropdowns.isEmpty()
        })}
        text={dropdownText}
        store={store}
        dropdowns={dropdowns}
        isExplanation={false}
        isGuessSubmitted={isGuessSubmitted}
        question={question}
      />
    </Fieldset>
  );
};

export default FillInTheBlankInputArea;

// MarkdownRendererV2 compatible dropdown
export const formatDropdowns = (dropdowns: any | null) => {
  const formattedDropdowns = !dropdowns
    ? []
    : dropdowns.map((info) => {
        // mardown is expecting the following methods
        // getId
        // getChoices
        const choices = info.choices.map((choice) => {
          return {...choice, getValue: () => choice.value, getId: () => choice.id};
        });
        return {
          ...info,
          choices,
          getId: () => info.id,
          getChoices: () => List(choices),
          getPosition: () => info.position
        };
      });

  return List(formattedDropdowns);
};
